import React, { useState, useEffect } from 'react';

const TODAY = new Date().toISOString().slice(0, 10);

const ACTION_STYLE = {
  SEGMENT_ENABLED:  { bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
  SEGMENT_DISABLED: { bg: '#fff5f5', color: '#dc2626', border: '#fecaca' },
  SSO_GENERATED:    { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
  FILE_GENERATED:   { bg: '#fff7ed', color: '#c2410c', border: '#fed7aa' },
  DEFAULT:          { bg: '#f8fafc', color: '#475569', border: '#e2e8f0' },
};

const STATUS_STYLE = {
  SUCCESS: { bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
  FAILED:  { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
  SKIPPED: { bg: '#f8fafc', color: '#94a3b8', border: '#e2e8f0' },
};

export default function AuditLogs() {
  const [adminLogs,   setAdminLogs]   = useState([]);
  const [dealerLogs,  setDealerLogs]  = useState([]);
  const [commLogs,    setCommLogs]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [activeTab,   setActiveTab]   = useState('admin');
  const [search,      setSearch]      = useState('');
  const [dateFrom,    setDateFrom]    = useState(TODAY);
  const [dateTo,      setDateTo]      = useState(TODAY);
  const [toast,       setToast]       = useState(null);
  const [modal,       setModal]       = useState(null); // { commId, commLog, recipients, loading }

  const token   = localStorage.getItem('nb_admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [aRes, dRes, cRes] = await Promise.all([
        fetch('/api/admin/control/logs',         { headers }),
        fetch('/api/admin/dealer-logs',           { headers }),
        fetch('/api/admin/control/communications',{ headers }),
      ]);
      const aData = await aRes.json();
      const dData = await dRes.json();
      const cData = await cRes.json();
      if (aData.success) setAdminLogs(aData.logs);
      if (dData.success) setDealerLogs(dData.logs);
      if (cData.success) setCommLogs(cData.communications);
    } catch { showToast('Failed to load logs.', 'error'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  const openRecipients = async (log) => {
    setModal({ commId: log.comm_id, commLog: log, recipients: [], loading: true });
    try {
      const res  = await fetch(`/api/admin/control/communications/${log.comm_id}/recipients`, { headers });
      const data = await res.json();
      setModal(m => ({ ...m, recipients: data.recipients || [], loading: false }));
    } catch {
      setModal(m => ({ ...m, loading: false }));
    }
  };

  const filterLogs = (logs) => logs.filter(log => {
    const ts          = new Date(log.created_at || log.sent_at);
    const matchSearch = !search || JSON.stringify(log).toLowerCase().includes(search.toLowerCase());
    const matchFrom   = !dateFrom || ts >= new Date(dateFrom);
    const matchTo     = !dateTo   || ts <= new Date(dateTo + 'T23:59:59');
    return matchSearch && matchFrom && matchTo;
  });

  const tabStyle = (tab) => ({
    padding: '9px 22px', cursor: 'pointer', fontSize: 12, fontWeight: 500,
    color: activeTab === tab ? '#1d4ed8' : '#64748b',
    borderBottom: `2px solid ${activeTab === tab ? '#2563eb' : 'transparent'}`,
    background: activeTab === tab ? '#f8faff' : 'transparent',
    transition: 'all 0.15s'
  });

  const inputStyle = {
    padding: '6px 10px', borderRadius: 6, border: '0.5px solid #e2e8f0',
    background: '#f8fafc', fontSize: 12, color: '#0f172a', outline: 'none'
  };

  const fmtDT = (ts) => ts ? new Date(ts).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  const filteredAdmin  = filterLogs(adminLogs);
  const filteredDealer = filterLogs(dealerLogs);
  const filteredComm   = filterLogs(commLogs);
  const totalToday     = [...adminLogs, ...dealerLogs].filter(l =>
    new Date(l.created_at).toDateString() === new Date().toDateString()
  ).length;

  const StatusBadge = ({ status }) => {
    const st = STATUS_STYLE[status] || STATUS_STYLE.SKIPPED;
    return (
      <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 10, background: st.bg, color: st.color, border: `0.5px solid ${st.border}`, whiteSpace: 'nowrap' }}>
        {status || 'SKIPPED'}
      </span>
    );
  };

  return (
    <div style={{ fontFamily: "'Segoe UI', Arial, sans-serif" }}>

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 9999, background: toast.type === 'error' ? '#fff5f5' : '#f0fdf4', border: `0.5px solid ${toast.type === 'error' ? '#fecaca' : '#bbf7d0'}`, color: toast.type === 'error' ? '#dc2626' : '#15803d', padding: '10px 18px', borderRadius: 8, fontSize: 13, boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
          {toast.msg}
        </div>
      )}

      {/* Recipients Detail Modal */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 950, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>

            {/* Modal Header */}
            <div style={{ padding: '18px 24px 14px', borderBottom: '0.5px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>
                  Communication Recipients — #{modal.commId}
                </div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                  {modal.commLog?.exchange} {modal.commLog?.segment} ·
                  {fmtDT(modal.commLog?.sent_at)} ·
                  {modal.recipients.length} clients
                </div>
              </div>
              <button onClick={() => setModal(null)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, color: '#374151', cursor: 'pointer' }}>✕ Close</button>
            </div>

            {/* Summary bar */}
            <div style={{ display: 'flex', gap: 0, borderBottom: '0.5px solid #e2e8f0', background: '#f8fafc' }}>
              {[
                { label: 'Total', value: modal.recipients.length, color: '#1d4ed8' },
                { label: 'Email ✓', value: modal.recipients.filter(r => r.email_status === 'SUCCESS').length, color: '#15803d' },
                { label: 'Email ✗', value: modal.recipients.filter(r => r.email_status === 'FAILED').length, color: '#dc2626' },
                { label: 'WhatsApp ✓', value: modal.recipients.filter(r => r.whatsapp_status === 'SUCCESS').length, color: '#15803d' },
                { label: 'WhatsApp ✗', value: modal.recipients.filter(r => r.whatsapp_status === 'FAILED').length, color: '#dc2626' },
              ].map((s, i) => (
                <div key={i} style={{ padding: '10px 20px', borderRight: '0.5px solid #e2e8f0' }}>
                  <div style={{ fontSize: 10, color: '#64748b', marginBottom: 2 }}>{s.label}</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: s.color }}>{s.value}</div>
                </div>
              ))}
            </div>

            {/* Modal Body */}
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {modal.loading ? (
                <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8' }}>Loading recipients...</div>
              ) : modal.recipients.length === 0 ? (
                <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8' }}>
                  No recipient details found. Details are captured for new communications only.
                </div>
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '100px 160px 180px 130px 110px 110px 1fr', padding: '10px 20px', background: '#f8fafc', borderBottom: '0.5px solid #e2e8f0', position: 'sticky', top: 0 }}>
                    {['UCC', 'Client Name', 'Email', 'Mobile', 'Email Status', 'WhatsApp', 'Error'].map(h => (
                      <div key={h} style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</div>
                    ))}
                  </div>
                  {modal.recipients.map((r, i) => (
                    <div key={r.id} style={{ display: 'grid', gridTemplateColumns: '100px 160px 180px 130px 110px 110px 1fr', padding: '11px 20px', alignItems: 'center', borderBottom: i < modal.recipients.length - 1 ? '0.5px solid #f1f5f9' : 'none', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a' }}>{r.ucc}</div>
                      <div style={{ fontSize: 11, color: '#374151' }}>{r.client_name || '—'}</div>
                      <div style={{ fontSize: 11, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.email || '—'}</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>{r.mobile || '—'}</div>
                      <StatusBadge status={r.email_status} />
                      <StatusBadge status={r.whatsapp_status} />
                      <div style={{ fontSize: 11, color: '#dc2626' }}>{r.error_message || '—'}</div>
                    </div>
                  ))}
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{ padding: '12px 24px', borderTop: '0.5px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: '#64748b' }}>
                {modal.recipients.filter(r => r.email_status === 'SUCCESS').length} emails delivered ·
                {modal.recipients.filter(r => r.whatsapp_status === 'SUCCESS').length} WhatsApp delivered
              </span>
              <button onClick={() => setModal(null)} style={{ padding: '7px 20px', borderRadius: 7, border: 'none', background: '#1d4ed8', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Admin Actions',  value: adminLogs.length,  color: '#1d4ed8' },
          { label: 'Dealer Actions', value: dealerLogs.length, color: '#7c3aed' },
          { label: 'Client Alerts',  value: commLogs.length,   color: '#c2410c' },
          { label: "Today's Events", value: totalToday,        color: '#15803d' },
        ].map(c => (
          <div key={c.label} style={{ background: '#fff', border: '0.5px solid #e2e8f0', borderRadius: 8, padding: '14px 16px' }}>
            <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{c.label}</div>
            <div style={{ fontSize: 24, fontWeight: 600, color: c.color }}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ background: '#fff', border: '0.5px solid #e2e8f0', borderRadius: 10, padding: '14px 20px', marginBottom: 16, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <input type="text" placeholder="Search logs..." value={search} onChange={e => setSearch(e.target.value)} style={{ ...inputStyle, width: 220 }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11, color: '#64748b' }}>From</span>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={inputStyle} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11, color: '#64748b' }}>To</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={inputStyle} />
        </div>
        <button onClick={() => { setSearch(''); setDateFrom(TODAY); setDateTo(TODAY); }}
          style={{ ...inputStyle, cursor: 'pointer', background: '#fff' }}>Reset</button>
        <button onClick={loadData}
          style={{ ...inputStyle, background: '#1d4ed8', color: '#fff', border: 'none', cursor: 'pointer' }}>↻ Refresh</button>
      </div>

      {/* Logs Panel */}
      <div style={{ background: '#fff', border: '0.5px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ display: 'flex', borderBottom: '0.5px solid #e2e8f0' }}>
          {[
            { key: 'admin',  label: 'Admin Logs',         count: filteredAdmin.length,  color: '#1d4ed8', bg: '#eff6ff' },
            { key: 'dealer', label: 'Dealer Logs',        count: filteredDealer.length, color: '#7c3aed', bg: '#f5f3ff' },
            { key: 'comm',   label: 'Communication Logs', count: filteredComm.length,   color: '#c2410c', bg: '#fff7ed' },
          ].map(t => (
            <div key={t.key} style={tabStyle(t.key)} onClick={() => setActiveTab(t.key)}>
              {t.label}
              <span style={{ marginLeft: 6, background: t.bg, color: t.color, padding: '1px 6px', borderRadius: 10, fontSize: 10 }}>{t.count}</span>
            </div>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8' }}>Loading logs...</div>
        ) : activeTab === 'admin' ? (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '60px 100px 160px 1fr 160px', padding: '10px 20px', background: '#f8fafc', borderBottom: '0.5px solid #e2e8f0' }}>
              {['#', 'Admin', 'Action', 'Details', 'Timestamp'].map(h => (
                <div key={h} style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</div>
              ))}
            </div>
            {filteredAdmin.length === 0 ? (
              <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8' }}>No admin logs found.</div>
            ) : filteredAdmin.map((log, i) => {
              const st = ACTION_STYLE[log.action] || ACTION_STYLE.DEFAULT;
              return (
                <div key={log.log_id} style={{ display: 'grid', gridTemplateColumns: '60px 100px 160px 1fr 160px', padding: '12px 20px', alignItems: 'center', borderBottom: i < filteredAdmin.length - 1 ? '0.5px solid #f1f5f9' : 'none', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>#{log.log_id}</div>
                  <div style={{ fontSize: 12, fontWeight: 500, color: '#0f172a' }}>{log.full_name || log.username || '—'}</div>
                  <div><span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: st.bg, color: st.color, border: `0.5px solid ${st.border}` }}>{log.action}</span></div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>{log.details || '—'}</div>
                  <div style={{ fontSize: 10, color: '#64748b' }}>{fmtDT(log.created_at)}</div>
                </div>
              );
            })}
          </>
        ) : activeTab === 'dealer' ? (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '60px 100px 120px 130px 1fr 160px', padding: '10px 20px', background: '#f8fafc', borderBottom: '0.5px solid #e2e8f0' }}>
              {['#', 'Dealer', 'UCC', 'Action', 'Details', 'Timestamp'].map(h => (
                <div key={h} style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</div>
              ))}
            </div>
            {filteredDealer.length === 0 ? (
              <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8' }}>No dealer logs found.</div>
            ) : filteredDealer.map((log, i) => (
              <div key={log.log_id} style={{ display: 'grid', gridTemplateColumns: '60px 100px 120px 130px 1fr 160px', padding: '12px 20px', alignItems: 'center', borderBottom: i < filteredDealer.length - 1 ? '0.5px solid #f1f5f9' : 'none', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>#{log.log_id}</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#7c3aed' }}>{log.dealer_id}</div>
                <div style={{ fontSize: 12, color: '#0f172a' }}>{log.ucc || '—'}</div>
                <div><span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: '#eff6ff', color: '#1d4ed8', border: '0.5px solid #bfdbfe' }}>{log.action}</span></div>
                <div style={{ fontSize: 11, color: '#64748b' }}>{log.details || '—'}</div>
                <div style={{ fontSize: 10, color: '#64748b' }}>{fmtDT(log.created_at)}</div>
              </div>
            ))}
          </>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '60px 120px 80px 60px 80px 1fr 120px 160px', padding: '10px 20px', background: '#f8fafc', borderBottom: '0.5px solid #e2e8f0' }}>
              {['#', 'Sent By', 'Exch', 'Seg', 'Channel', 'Message', 'Recipients', 'Sent At'].map(h => (
                <div key={h} style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</div>
              ))}
            </div>
            {filteredComm.length === 0 ? (
              <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8' }}>No communication logs found.</div>
            ) : filteredComm.map((log, i) => (
              <div key={log.comm_id} style={{ display: 'grid', gridTemplateColumns: '60px 120px 80px 60px 80px 1fr 120px 160px', padding: '12px 20px', alignItems: 'center', borderBottom: i < filteredComm.length - 1 ? '0.5px solid #f1f5f9' : 'none', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>#{log.comm_id}</div>
                <div style={{ fontSize: 12, fontWeight: 500, color: '#0f172a' }}>{log.sent_by || 'Admin'}</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>{log.exchange || '—'}</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>{log.segment  || '—'}</div>
                <div><span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 10, background: '#f0fdf4', color: '#15803d', border: '0.5px solid #bbf7d0', display: 'inline-block' }}>{log.message_type || 'BOTH'}</span></div>
                <div style={{ fontSize: 11, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 8 }}>{log.message_content || '—'}</div>
                <div
                  onClick={() => openRecipients(log)}
                  onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                  onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
                  style={{ fontSize: 12, fontWeight: 700, color: '#1d4ed8', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 2 }}>
                  {log.recipients_count || 0} clients →
                </div>
                <div style={{ fontSize: 10, color: '#64748b' }}>{fmtDT(log.sent_at)}</div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}